import type { useSingleton } from "@tippyjs/react";
import { useSetAtom } from "jotai/index";
import { useEffect, useState } from "react";
import { canv } from "@/canv/global";
import type { PresentationWidget } from "@/elements/Presentation/Presentation";
import PresentationHeaderIcon from "@/icons/presentation/PresentationHeaderIcon.svg?react";
import { headerActiveButtonAtom, presentationScreenVisibility } from "@/interface/AppStore";
import { BottomBarItem } from "@/interface/BottomBar/BottomRightBar";
import { MembersCount } from "@/interface/HeaderBar/RightBar/Presentation/MembersCount";
import { Tooltip } from "@/interface/ui/Tooltip";
import styles from "./PresentationButton.module.scss";

type PresentationButtonProps = {
    singletoneTarget: ReturnType<typeof useSingleton>[1];
    className?: string;
    isActive: boolean;
    activePresentationUID: string;
};

export const PresentationButton = ({
    singletoneTarget,
    className,
    isActive,
    activePresentationUID,
}: PresentationButtonProps) => {
    const [hasActivePresentation, setHasActivePresentation] = useState(false);
    const [count, setCount] = useState(0);
    const setActiveHeaderButton = useSetAtom(headerActiveButtonAtom);
    const setIsVisible = useSetAtom(presentationScreenVisibility);

    const handleClick = () => {
        canv.appInterface.commentsWindow.close();
        canv.appInterface.voteWindow.close();

        if (activePresentationUID) {
            const presentation = canv.canvasController.FindByUID(
                activePresentationUID,
            ) as PresentationWidget;
            if (presentation) {
                presentation.join();
            }
            setIsVisible(true);
        } else {
            setActiveHeaderButton(isActive ? null : "presentation");
        }
    };

    useEffect(() => {
        setHasActivePresentation(!!activePresentationUID);
        const presentation = canv.canvasController.FindByUID(
            activePresentationUID,
        ) as PresentationWidget;
        if (presentation) {
            setCount(presentation.viewerUIDs.length);
        }
    }, [activePresentationUID]);

    useEffect(() => {
        const handle = () => {
            const presentation = canv.canvasController.FindByUID(
                activePresentationUID,
            ) as PresentationWidget;
            if (presentation) {
                setCount(presentation.viewerUIDs.length);
            }
        };
        document.addEventListener("updatePresentationMembersCountMessage", handle);
        return () => {
            document.removeEventListener("updatePresentationMembersCountMessage", handle);
        };
    }, [activePresentationUID]);

    return (
        <Tooltip
            singleton={singletoneTarget}
            content={activePresentationUID ? "Присоединиться к презентации" : "Презентации"}
        >
            <div className={styles.buttonWrapper}>
                <BottomBarItem
                    active={isActive || hasActivePresentation}
                    className={className}
                    onClick={handleClick}
                >
                    <PresentationHeaderIcon />
                </BottomBarItem>
                {hasActivePresentation && <MembersCount count={count} />}
            </div>
        </Tooltip>
    );
};
